import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, Plus, Loader2, Trash2, Eye, EyeOff, UserCheck, TrendingUp, DollarSign,
  Copy, Link as LinkIcon, Mail, Percent, FileText, KeyRound, Power, PowerOff, Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/adminApi";
import { PARTNERS as REGISTERED_PARTNERS } from "@/config/partners";

const REF_BASE = "mimaquinasegura.cl";
const fmtCLP = (n) => (n == null ? "$0" : `$${Number(n).toLocaleString("es-CL")}`);
const buildRefLink = (code) => (code ? `https://${REF_BASE}/Ref${code}` : `https://${REF_BASE}/RefCODIGO`);
const copyLink = async (text) => {
  try { await navigator.clipboard.writeText(text); toast.success("Link copiado"); }
  catch { toast.error("No se pudo copiar"); }
};

// Partners co-brandeados disponibles (viene del registro central del frontend).
const PARTNERS = REGISTERED_PARTNERS.map((p) => ({ code: p.code, name: p.name, path: p.path }));

export function AffiliatesManager({ onChange }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPwd, setShowPwd] = useState({});
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", code: "", commission_pct: "", partner: "", parent_code: "",
    email: "", username: "", password: "",
  });
  const [editing, setEditing] = useState(null); // { aff, tab: 'comision'|'clave'|'datos'|'email' }
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/affiliates"); setList(r.data); }
    catch { toast.error("No se pudo cargar la lista de afiliados"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => list.reduce((acc, a) => {
    acc.afiliados += 1;
    acc.leads += Number(a.leads_total || 0);
    acc.emitidos += Number(a.leads_ganado || 0);
    acc.por_cobrar += Number(a.comision_por_cobrar || 0);
    return acc;
  }, { afiliados: 0, leads: 0, emitidos: 0, por_cobrar: 0 }), [list]);

  const submitCreate = async () => {
    if (!form.name.trim()) return toast.error("Nombre requerido");
    if (!form.code.trim()) return toast.error("Código requerido");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.toUpperCase(),
        email: form.email.trim() || null,
        username: form.username.trim() || null,
        commission_pct: form.commission_pct === "" ? null : Number(form.commission_pct),
        partner: form.partner || null,
        parent_code: form.parent_code || null,
      };
      if (form.password) payload.password = form.password;
      await api.post("/admin/affiliates", payload);
      toast.success("Afiliado creado");
      setForm({ name: "", code: "", commission_pct: "", partner: "", parent_code: "", email: "", username: "", password: "" });
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear el afiliado");
    } finally { setSaving(false); }
  };

  const quickUpdate = async (aff, patch) => {
    try {
      await api.patch(`/admin/affiliates/${aff.id}`, patch);
      toast.success("Actualizado");
      await load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar"); }
  };

  const remove = async (aff) => {
    if (!window.confirm(`¿Eliminar al afiliado ${aff.name}? Esta acción no se puede deshacer.`)) return;
    try { await api.delete(`/admin/affiliates/${aff.id}`); toast.success("Eliminado"); await load(); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar"); }
  };

  const goToLeads = (aff) => {
    // Navegar al panel de leads y filtrar por origen (query param).
    navigate(`/admin?origen=${encodeURIComponent(aff.code)}`);
  };

  return (
    <div>
      {/* Header + stats */}
      <div className="mb-6">
        <h2 className="font-display text-3xl font-bold text-brand-ink">Central de Afiliados</h2>
        <p className="mt-1 text-sm text-gray-500">Crea links de referido (ej: <span className="font-mono text-brand-ink">/RefFC</span>) y revisa los resultados de cada afiliado.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Users} label="Afiliados" value={totals.afiliados} accent="bg-blue-100 text-blue-700" />
        <StatCard icon={TrendingUp} label="Leads referidos" value={totals.leads} accent="bg-indigo-100 text-indigo-700" />
        <StatCard icon={UserCheck} label="Convertidos" value={totals.emitidos} accent="bg-emerald-100 text-emerald-700" />
        <div data-testid="stat-por-cobrar" className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5 shadow-sm">
          <p className="font-display text-3xl font-bold text-emerald-700">{fmtCLP(totals.por_cobrar)}</p>
          <p className="mt-1 text-sm text-gray-600">Comisión por cobrar</p>
        </div>
      </div>

      {/* Formulario inline: Crear afiliado / link */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 mb-6 shadow-sm">
        <div className="flex items-center gap-2 mb-5">
          <LinkIcon className="h-5 w-5 text-brand-yellow" />
          <h3 className="font-display text-lg font-bold text-brand-ink">Crear afiliado / link de referido</h3>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <Label className="text-sm font-medium text-brand-ink">Nombre del afiliado</Label>
            <Input data-testid="aff-form-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Franco Contreras" className="h-11 mt-1 border-gray-300" />
          </div>
          <div>
            <Label className="text-sm font-medium text-brand-ink">Código / Iniciales</Label>
            <Input data-testid="aff-form-code" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") }))} placeholder="FC" maxLength={16} className="h-11 mt-1 border-gray-300 font-mono" />
          </div>
          <div>
            <Label className="text-sm font-medium text-brand-ink">Comisión %</Label>
            <Input data-testid="aff-form-pct" type="number" value={form.commission_pct} onChange={(e) => setForm((f) => ({ ...f, commission_pct: e.target.value }))} placeholder="10" className="h-11 mt-1 border-gray-300" />
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-sm font-medium text-brand-ink">Sitio co-brandeado (opcional)</Label>
          <select data-testid="aff-form-partner" value={form.partner} onChange={(e) => setForm((f) => ({ ...f, partner: e.target.value }))} className="mt-1 h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-brand-ink focus:outline-none focus:ring-1 focus:ring-brand-yellow">
            <option value="">— Sin sitio (usa su link de referido /Ref{form.code || "CÓDIGO"}) —</option>
            {PARTNERS.map((p) => <option key={p.code} value={p.code}>{p.name} (/{p.path})</option>)}
          </select>
          <p className="mt-1 text-xs text-gray-500">Con sitio: el afiliado verá los leads que entran por ese sitio co-brandeado. Sin sitio: verá los de su propio link de referido.</p>
        </div>

        <div className="mt-4">
          <Label className="text-sm font-medium text-brand-ink">Afiliado padre (opcional)</Label>
          <select data-testid="aff-form-parent" value={form.parent_code} onChange={(e) => setForm((f) => ({ ...f, parent_code: e.target.value }))} className="mt-1 h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-brand-ink focus:outline-none focus:ring-1 focus:ring-brand-yellow">
            <option value="">— Sin padre (afiliado principal) —</option>
            {list.filter((a) => a.code !== form.code).map((a) => <option key={a.code} value={a.code}>{a.name} ({a.code})</option>)}
          </select>
          <p className="mt-1 text-xs text-gray-500">Si eliges un padre, este afiliado quedará &quot;debajo&quot; de él: el padre verá a este afiliado y sus ventas en su propio panel.</p>
        </div>

        <div className="mt-4">
          <Label className="text-sm font-medium text-brand-ink">Email del afiliado <span className="text-xs text-gray-400">(para avisarle cuando gane una venta)</span></Label>
          <Input data-testid="aff-form-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="franco@ejemplo.cl" className="h-11 mt-1 border-gray-300" />
        </div>

        <div className="mt-5 rounded-xl bg-gray-50 border border-gray-200 p-4">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
            <KeyRound className="h-3.5 w-3.5" /> Acceso al portal del afiliado (opcional)
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-sm text-brand-ink">Usuario</Label>
              <Input data-testid="aff-form-username" value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value.toLowerCase() }))} placeholder="franco.c" className="h-11 mt-1 border-gray-300" />
            </div>
            <div>
              <Label className="text-sm text-brand-ink">Clave</Label>
              <Input data-testid="aff-form-password" type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="clave para el afiliado" className="h-11 mt-1 border-gray-300" />
            </div>
          </div>
          <p className="mt-3 text-xs text-gray-500">Con usuario y clave, el afiliado podrá entrar a <span className="font-mono">https://{REF_BASE}/afiliado</span> a ver sus leads y comisión.</p>
        </div>

        <div className="mt-5 flex justify-start">
          <Button
            onClick={submitCreate}
            disabled={saving}
            data-testid="aff-form-submit"
            className="h-11 rounded-full bg-brand-ink hover:bg-brand-inkHover text-white font-semibold px-6 shadow"
          >
            {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Creando…</> : <><Plus className="h-4 w-4 mr-2" /> Crear afiliado y link</>}
          </Button>
        </div>
      </div>

      {/* Lista de afiliados en cards */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-brand-yellow" /></div>
      ) : list.length === 0 ? (
        <div className="text-center py-12 text-gray-500 bg-white rounded-2xl border border-gray-200">
          <Users className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          Aún no hay afiliados. Crea el primero arriba.
        </div>
      ) : (
        <div className="space-y-4">
          {list.map((a) => (
            <AffiliateCard
              key={a.id}
              aff={a}
              affiliates={list}
              showPwd={!!showPwd[a.id]}
              togglePwd={() => setShowPwd((s) => ({ ...s, [a.id]: !s[a.id] }))}
              onEdit={(tab) => setEditing({ aff: a, tab })}
              onDelete={() => remove(a)}
              onQuick={(patch) => quickUpdate(a, patch)}
              onGoToLeads={() => goToLeads(a)}
            />
          ))}
        </div>
      )}

      {editing && (
        <EditDialog
          aff={editing.aff}
          tab={editing.tab}
          onClose={() => setEditing(null)}
          onSaved={() => { load(); onChange?.(); setEditing(null); }}
        />
      )}
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div data-testid={`stat-${label}`} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
    <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${accent}`}>
      <Icon className="h-5 w-5" />
    </div>
    <p className="mt-4 font-display text-3xl font-bold text-brand-ink">{value}</p>
    <p className="mt-1 text-sm text-gray-500">{label}</p>
  </div>
);

function AffiliateCard({ aff, affiliates, showPwd, togglePwd, onEdit, onDelete, onQuick, onGoToLeads }) {
  const refLink = buildRefLink(aff.code);
  const otherAffs = affiliates.filter((x) => x.code !== aff.code);
  return (
    <div data-testid={`aff-card-${aff.id}`} className={`rounded-2xl border border-gray-200 bg-white p-5 shadow-sm ${aff.active ? "" : "opacity-60"}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        {/* Info izquierda */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <h4 className="font-display text-xl font-bold text-brand-ink">{aff.name}</h4>
            <span className="font-mono text-[11px] font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">{aff.code}</span>
            {aff.partner && <span className="text-[11px] font-semibold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Building2 className="h-3 w-3" />Sitio: /{aff.partner.toLowerCase()}</span>}
            {aff.username && <span className="text-[11px] font-semibold bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1">Portal: {aff.username}</span>}
            {aff.parent_code && <span className="text-[11px] font-semibold bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">Padre: {aff.parent_code}</span>}
          </div>
          {aff.has_login && aff.password && (
            <div className="mb-1 inline-flex items-center gap-1 text-xs text-gray-500">
              <KeyRound className="h-3 w-3" />
              <span className="font-mono">{showPwd ? aff.password : "••••••"}</span>
              <button onClick={togglePwd} className="text-gray-400 hover:text-brand-ink" data-testid={`aff-pwd-toggle-${aff.id}`}>
                {showPwd ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
              </button>
            </div>
          )}
          {aff.email && (
            <a href={`mailto:${aff.email}`} className="mt-1 inline-flex items-center gap-1 text-xs text-brand-ink hover:underline">
              <Mail className="h-3 w-3" /> {aff.email}
            </a>
          )}
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[11px] text-gray-500 truncate max-w-[280px]" title={refLink}>{refLink}</span>
            <button
              onClick={() => copyLink(refLink)}
              data-testid={`aff-copy-${aff.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-brand-yellow/10 border border-brand-yellow/40 text-brand-ink px-3 py-0.5 text-[11px] font-semibold hover:bg-brand-yellow/20 transition-colors"
            >
              <Copy className="h-3 w-3" /> Copiar link
            </button>
          </div>
          <div className="mt-3 text-xs text-gray-500">
            <span className="font-display text-2xl font-bold text-brand-ink mr-1">{aff.leads_total}</span> leads
            <span className="ml-3 font-display text-2xl font-bold text-brand-ink mr-1">{aff.leads_ganado}</span> emitidos
          </div>
          <div className="mt-2 text-xs">
            <span className="text-gray-500">Por cobrar:</span> <span className="font-bold text-emerald-700">{fmtCLP(aff.comision_por_cobrar)}</span>
            <span className="ml-4 text-gray-500">Pagada:</span> <span className="font-bold text-brand-ink">{fmtCLP(aff.comision_pagada)}</span>
          </div>
        </div>

        {/* Acciones derecha */}
        <div className="flex flex-wrap items-center gap-2 justify-end">
          <ActionBtn testid={`aff-view-leads-${aff.id}`} onClick={onGoToLeads} icon={Eye} label="Ver leads" primary />
          {aff.email && <ActionBtn testid={`aff-email-${aff.id}`} onClick={() => window.location.href = `mailto:${aff.email}`} icon={Mail} label="Email" />}
          <ActionBtn testid={`aff-pct-${aff.id}`} onClick={() => onEdit("comision")} icon={Percent} label="Comisión" />
          <ActionBtn testid={`aff-data-${aff.id}`} onClick={() => onEdit("datos")} icon={FileText} label="Datos" />

          <select
            data-testid={`aff-partner-${aff.id}`}
            value={aff.partner || ""}
            onChange={(e) => onQuick({ partner: e.target.value || null })}
            className="h-8 rounded-md border border-gray-300 bg-white px-2 text-xs text-brand-ink focus:outline-none focus:ring-1 focus:ring-brand-yellow"
          >
            <option value="">Sin sitio</option>
            {PARTNERS.map((p) => <option key={p.code} value={p.code}>/{p.path}</option>)}
          </select>
          <select
            data-testid={`aff-parent-${aff.id}`}
            value={aff.parent_code || ""}
            onChange={(e) => onQuick({ parent_code: e.target.value || "" })}
            className="h-8 rounded-md border border-gray-300 bg-white px-2 text-xs text-brand-ink focus:outline-none focus:ring-1 focus:ring-brand-yellow"
          >
            <option value="">Sin padre</option>
            {otherAffs.map((o) => <option key={o.code} value={o.code}>{o.code}</option>)}
          </select>
          <ActionBtn testid={`aff-key-${aff.id}`} onClick={() => onEdit("clave")} icon={KeyRound} label="Clave" />
          <ActionBtn
            testid={`aff-active-${aff.id}`}
            onClick={() => onQuick({ active: !aff.active })}
            icon={aff.active ? Power : PowerOff}
            label={aff.active ? "Desactivar" : "Activar"}
          />
          <button onClick={onDelete} data-testid={`aff-delete-${aff.id}`} className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-rose-200 bg-white text-rose-600 hover:bg-rose-50 transition-colors" title="Eliminar">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

const ActionBtn = ({ icon: Icon, label, onClick, testid, primary }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`inline-flex items-center gap-1 h-8 rounded-md border px-2.5 text-xs font-semibold transition-colors ${
      primary
        ? "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
        : "border-gray-200 bg-white text-gray-700 hover:border-brand-yellow hover:text-brand-ink"
    }`}
  >
    <Icon className="h-3.5 w-3.5" /> {label}
  </button>
);

function EditDialog({ aff, tab, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: aff.name || "",
    email: aff.email || "",
    commission_pct: aff.commission_pct ?? "",
    username: aff.username || "",
    password: "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (tab === "comision") payload.commission_pct = form.commission_pct === "" ? null : Number(form.commission_pct);
      else if (tab === "clave") {
        payload.username = form.username.trim() || null;
        if (form.password) payload.password = form.password;
      } else {
        payload.name = form.name.trim();
        payload.email = form.email.trim() || null;
        payload.commission_pct = form.commission_pct === "" ? null : Number(form.commission_pct);
      }
      await api.patch(`/admin/affiliates/${aff.id}`, payload);
      toast.success("Actualizado");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo"); }
    finally { setSaving(false); }
  };
  const title = tab === "comision" ? "Editar comisión" : tab === "clave" ? "Editar usuario y clave" : "Datos del afiliado";
  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="aff-edit-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{aff.name} · <span className="font-mono">{aff.code}</span></DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          {tab === "comision" && (
            <div>
              <Label>Comisión %</Label>
              <Input data-testid="aff-edit-pct" type="number" value={form.commission_pct} onChange={(e) => setForm((f) => ({ ...f, commission_pct: e.target.value }))} className="h-11 mt-1" />
            </div>
          )}
          {tab === "clave" && (
            <>
              <div>
                <Label>Usuario</Label>
                <Input data-testid="aff-edit-username" value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value.toLowerCase() }))} className="h-11 mt-1" />
              </div>
              <div>
                <Label>Nueva contraseña <span className="text-xs text-gray-400">(dejar vacía para no cambiar)</span></Label>
                <Input data-testid="aff-edit-password" type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className="h-11 mt-1" />
              </div>
            </>
          )}
          {tab === "datos" && (
            <>
              <div>
                <Label>Nombre</Label>
                <Input data-testid="aff-edit-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="h-11 mt-1" />
              </div>
              <div>
                <Label>Email</Label>
                <Input data-testid="aff-edit-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="h-11 mt-1" />
              </div>
              <div>
                <Label>Comisión %</Label>
                <Input type="number" value={form.commission_pct} onChange={(e) => setForm((f) => ({ ...f, commission_pct: e.target.value }))} className="h-11 mt-1" />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="aff-edit-save" className="bg-brand-ink hover:bg-brand-inkHover text-white">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Guardando</> : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
